import { getSessionId } from '../utils/sessionId';
import vendorConfig from '../vendors/vendorConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4003/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png'];

// ============ Helpers ============

async function request(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-session-id': getSessionId(),
      'x-vendor-id': vendorConfig.vendorId,
      ...options.headers,
    },
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || `Request failed: ${res.status}`);
  }
  return data;
}

async function requestMultipart(path, formData) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'x-session-id': getSessionId(),
      'x-vendor-id': vendorConfig.vendorId,
    },
    body: formData,
    // No Content-Type header — browser sets it with boundary
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || `Upload failed: ${res.status}`);
  }
  return data;
}

function validateFile(file, allowedTypes = ALLOWED_FILE_TYPES) {
  if (!file) throw new Error('No file selected');
  if (file.size > MAX_FILE_SIZE) throw new Error('File too large. Maximum size is 10MB.');
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type. Allowed: ${allowedTypes.map(t => t.split('/')[1]).join(', ')}`);
  }
  return true;
}

// ============ Authentication Endpoints ============

export async function sendLoginOTP(email) {
  return request('/auth/send-otp', {
    method: 'POST',
    body: JSON.stringify({ email })
  });
}

export async function verifyLoginOTP(email, otp) {
  return request('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp })
  });
}

// ============ Visa Endpoints ============

export async function searchVisas(query = '', filters = {}) {
  const params = new URLSearchParams();
  if (query) params.set('q', query.trim());
  if (filters.category) params.set('category', filters.category);
  if (filters.country) params.set('country', filters.country);
  if (filters.citizenship) params.set('citizenship', filters.citizenship);
  return request(`/visas/search?${params.toString()}`);
}

export async function getVisaDetails(visaId) {
  return request(`/visas/${visaId}`);
}

export async function getFeaturedVisas() {
  return request('/visas/featured');
}

export async function getVisaCategories() {
  return request('/visas/categories');
}

export async function getVisaCountries() {
  return request('/visas/countries');
}

export async function getAllVisas(page = 1, limit = 12) {
  return request(`/visas?page=${page}&limit=${limit}`);
}

// ============ Application Endpoints ============

export async function getRequiredDocs(visaId) {
  return request(`/applications/visa/${visaId}/documents`);
}

export async function uploadDocument(file, documentTypeId) {
  validateFile(file);
  const formData = new FormData();
  formData.append('document', file);
  formData.append('documentTypeId', documentTypeId);
  return requestMultipart('/applications/documents/upload', formData);
}

export async function confirmDocument(documentId, extractedData) {
  return request(`/applications/documents/${documentId}/confirm`, {
    method: 'POST',
    body: JSON.stringify(extractedData),
  });
}

export async function getVisaPricing(visaId) {
  return request(`/applications/visa/${visaId}/pricing`);
}

export async function submitApplication(data) {
  return request('/applications/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getApplications() {
  return request('/applications');
}

export async function getApplicationDetail(applicationId) {
  return request(`/applications/${applicationId}`);
}

export async function getUserDocuments(documentTypeIds) {
  const params = documentTypeIds ? `?documentTypeIds=${documentTypeIds.join(',')}` : '';
  return request(`/applications/documents${params}`);
}

export async function getDocumentPreview(docId) {
  return request(`/applications/documents/${docId}/preview`);
}

// ============ Payment Endpoints ============

export async function createPaymentIntent(visaConfigId, extras = {}) {
  return request('/payments/create-intent', {
    method: 'POST',
    body: JSON.stringify({ visaConfigId, ...extras }),
  });
}

export async function confirmPayment(data) {
  return request('/payments/confirm', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function initCryptoPayment(data) {
  return request('/payments/crypto/init', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function confirmCryptoPayment(data) {
  return request('/payments/crypto/confirm', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCryptoCurrencies() {
  return request('/payments/crypto/currencies');
}

export async function getCryptoQuote(token, network, usdAmount) {
  return request(`/payments/crypto/quote?token=${token}&network=${network}&usdAmount=${usdAmount}`);
}

export async function getHelioConfig() {
  return request('/payments/helio/config');
}

export async function confirmHelioPayment(data) {
  return request('/payments/helio/confirm', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function confirmHelioBusinessPayment(data) {
  return request('/payments/helio/confirm-business', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============ Photo Processing ============

export async function processPhoto(file, countryCode) {
  validateFile(file, ALLOWED_PHOTO_TYPES);
  const formData = new FormData();
  formData.append('photo', file);
  formData.append('countryCode', countryCode);
  return requestMultipart('/photo-processing/process', formData);
}

export async function approvePhoto(data) {
  return request('/photo-processing/approve', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getUserPhotos() {
  return request('/photo-processing/photos');
}

export async function getPhotoPreview(photoId) {
  return request(`/photo-processing/photos/${photoId}/preview`);
}

// ============ Consent ============

export async function getConsentStatus() {
  return request('/consent/status');
}

export async function grantAdminConsent(data) {
  return request('/consent/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function grantAgentConsent(data) {
  return request('/consent/agent', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============ Vault / Global Pass ============

export async function getVaultProfile() {
  return request('/global-pass/profile');
}

export async function getVaultDocuments() {
  return request('/global-pass/documents');
}

export async function uploadVaultDocument(file, documentTypeId) {
  validateFile(file);
  const formData = new FormData();
  formData.append('document', file);
  formData.append('documentTypeId', documentTypeId);
  return requestMultipart('/global-pass/upload-document', formData);
}

export async function downloadVaultDocument(documentId) {
  const response = await fetch(`${API_URL}/global-pass/documents/${documentId}/download`, {
    credentials: 'include',
    headers: { 'x-vendor-id': vendorConfig.vendorId },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Download failed');
  }
  return response;
}

export async function confirmVaultDocument(documentId, data) {
  return request(`/global-pass/documents/${documentId}/confirm`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteVaultDocument(documentId) {
  return request(`/global-pass/documents/${documentId}`, {
    method: 'DELETE',
  });
}

export async function deleteApplication(applicationId) {
  return request(`/applications/${applicationId}`, {
    method: 'DELETE',
  });
}

// ============ Business Registration ============

export async function getJurisdictions() {
  return request('/business/jurisdictions');
}

export async function getJurisdictionDetails(jurisdictionId) {
  return request(`/business/jurisdictions/${jurisdictionId}`);
}

export async function getBusinessActivities(jurisdictionId) {
  return request(`/business/jurisdictions/${jurisdictionId}/activities`);
}

export async function getBusinessPricing(jurisdictionId) {
  return request(`/business/pricing/${jurisdictionId}`);
}

export async function createBusinessRegistration(data) {
  return request('/business/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitBusinessRegistration(registrationId, data = {}) {
  return request(`/business/register/${registrationId}/submit`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getBusinessRegistrations() {
  return request('/business/registrations');
}

export async function getBusinessRegistrationDetail(registrationId) {
  return request(`/business/registrations/${registrationId}`);
}

export async function pollBusinessRegistrationStatus(registrationId) {
  return request(`/business/registrations/${registrationId}/status`);
}

export async function getBusinessRegistrationDocuments(registrationId) {
  return request(`/business/registrations/${registrationId}/documents`);
}

export async function downloadBusinessDocument(registrationId, docId) {
  return request(`/business/registrations/${registrationId}/documents/${docId}/download`);
}

export async function createBusinessPaymentIntent(registrationId) {
  return request(`/business/registrations/${registrationId}/payment`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ============ Chat History ============

export async function getConversations() {
  return request('/chat/conversations');
}

export async function getConversation(conversationId) {
  return request(`/chat/conversations/${conversationId}`);
}

export async function createConversation(data) {
  return request('/chat/conversations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateConversation(conversationId, data) {
  return request(`/chat/conversations/${conversationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteConversation(conversationId) {
  return request(`/chat/conversations/${conversationId}`, {
    method: 'DELETE',
  });
}

// ============ Chat AI ============

export async function aiDetectIntent(message) {
  return request('/chat/detect-intent', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function aiTranslate(text, targetLang) {
  return request('/chat/translate', {
    method: 'POST',
    body: JSON.stringify({ text, targetLang }),
  });
}

// ============ Agent (Agentic Chat) ============

export async function sendAgentMessage({ conversationId, message, appCtx }) {
  return request('/agent/message', {
    method: 'POST',
    body: JSON.stringify({ conversationId, message, appCtx }),
  });
}

export async function loadConversationState(conversationId) {
  return request(`/agent/conversations/${conversationId}/state`);
}

export async function syncConversation({ messages, appCtx }) {
  return request('/agent/sync-conversation', {
    method: 'POST',
    body: JSON.stringify({ messages, appCtx }),
  });
}

export async function transcribeAudio(audioBlob) {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  return requestMultipart('/agent/transcribe', formData);
}

// ============ Session Merge ============

export async function mergeSession(sessionId) {
  return request('/agent/merge-session', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });
}

// ============ Auth ============

export async function getCurrentUser() {
  return request('/auth/me');
}

// ============ User Profile ============

export async function updateWhatsappNumber(whatsappNumber) {
  return request('/user/whatsapp', {
    method: 'PUT',
    body: JSON.stringify({ whatsappNumber }),
  });
}

// ============ Invite Codes ============

export async function validateInviteCode(code) {
  return request('/invite-code/validate', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function linkInviteCode() {
  return request('/invite-code/link', { method: 'POST' });
}

// ============ Feedback ============

export async function submitFeedback({ rating, description }) {
  return request('/feedback', {
    method: 'POST',
    body: JSON.stringify({ rating, description }),
  });
}
